<template>
  <div class="p-the-b01-search-panel">
    <template v-if="currentCategoryChild.id">
      <CommonHeading
        :class="[
          showMainCategoryActive ? `active_category` : 'u-text-color--sub'
        ]"
      >
        {{ currentCategory.label }}
      </CommonHeading>
      <div class="p-the-b01-search-panel__categories">
        <li
          v-for="child in currentCategory.children"
          :key="child.id"
          class="p-the-b01-search-panel__categories__item"
        >
          <a
            :class="[
              child.id === currentCategoryChild.id && `u-text-color--red`
            ]"
            :style="[
              child.id === currentCategoryChild.id ? { color: 'red' } : {}
            ]"
            @click="clickOnSubcategory(child.id)"
          >
            {{ child.label }}
          </a>
        </li>
      </div>
    </template>
    <Form
      v-slot="{ handleSubmit }"
      ref="form"
      :validation-schema="schema"
      class="p-the-b01-search-panel__form"
      @submit="handleSubmit(submit)"
    >
      <input type="hidden" name="keyword" :value="searchKeyword" />

      <div class="p-the-b01-search-panel__form__selects">
        <InputText
          id="searchFrom"
          v-model="searchFrom"
          type="number"
          name="searchFrom"
          font-family="roboto"
          color="#616161"
          :disabled="disableMinMaxInputs"
          class="p-the-b01-search-panel__form__select"
          min="0"
          @input="changeFrom($event)"
        />
        <span class="p-the-b01-search-panel__form__range">〜</span>
        <InputText
          id="searchTo"
          v-model="searchTo"
          type="number"
          name="searchTo"
          font-family="roboto"
          color="#616161"
          :disabled="disableMinMaxInputs"
          class="p-the-b01-search-panel__form__select"
          min="0"
          @input="changeTo($event)"
        />
      </div>

      <div style="margin-left: 12px">
        <ValidationErrorText v-if="showInputValidationError">
          単価は半角数字で設定してください
        </ValidationErrorText>
      </div>

      <div class="p-the-b01-search-panel__form__checkbox">
        <InputCheckBox
          id="isUsableRange"
          v-model="internalIsUsableRange"
          name="isUsableRange"
          @checked="onCheck"
        >
          <span class="p-the-b01-search-panel__form__checkbox__label">
            現在のポイント内で探す
          </span>
        </InputCheckBox>
      </div>

      <div class="p-the-b01-search-panel__form__submit">
        <ButtonMain type="submit" kind="secondary">検索</ButtonMain>
      </div>
    </Form>
  </div>
</template>

<script lang="ts">
import { mapState } from "vuex";
import { Options, Vue } from "vue-class-component";
import { Form } from "vee-validate";
import * as yup from "yup";

import InputText from "@/common/components/atoms/InputText.vue";
import InputCheckBox from "@/common/components/atoms/InputCheckBox.vue";
import CommonHeading from "@/common/components/atoms/CommonHeading.vue";
import ButtonMain from "@/common/components/atoms/ButtonMain.vue";
import ValidationErrorText from "@/common/components/atoms/ValidationErrorText.vue";

import commons from "@/common/store/modules/commons";
import LocalStorage from "@/general/utils/storage";

@Options({
  components: {
    InputText,
    InputCheckBox,
    CommonHeading,
    ButtonMain,
    ValidationErrorText,
    Form
  },
  props: {
    currentCategory: Object,
    currentCategoryChild: Object,
    searchKeyword: String,
    maxPointSearchable: Number,
    searchFrom: { type: String, default: "" },
    searchTo: { type: String, default: "" },
    isUsableRange: { type: Boolean, default: false },
    haveItem: Boolean,
    showMainCategoryActive: Boolean
  }
})
export default class TheB01SearchPanel extends Vue {
  currentCategory!: any;
  currentCategoryChild!: any;
  searchKeyword!: string;
  maxPointSearchable!: number;
  searchFrom!: string;
  searchTo!: string;
  isUsableRange!: boolean;
  haveItem!: boolean;
  showMainCategoryActive!: boolean;

  internalIsUsableRange = false;
  disableMinMaxInputs = false;
  showInputValidationError = false;

  schema = yup.object({
    searchFrom: yup
      .number()
      .typeError("${label}は数字で入力してください。")
      .min(0, "${label}は0以上で入力してください。")
      .label("単価（下限）"),
    searchTo: yup
      .number()
      .typeError("${label}は数字で入力してください。")
      .min(0, "${label}は0以上で入力してください。")
      .label("単価（上限）")
  });

  get activeColor() {
    return commons.OrgPageSpecific?.page_color;
  }

  get color() {
    return commons.OrgPageSpecific?.page_color;
  }

  get point() {
    return commons.OrgPageSpecific?.point_name;
  }

  onCheck() {
    this.internalIsUsableRange = !this.internalIsUsableRange;
    if (this.internalIsUsableRange) {
      this.disableMinMaxInputs = true;
      LocalStorage.setItem("usableRange", "1");
    } else {
      this.disableMinMaxInputs = false;
      LocalStorage.removeItem("usableRange");
    }
  }

  submit() {
    const userPoints = this.getUserPoints();
    if (
      (parseInt(this.searchFrom) < 0 ||
        parseInt(this.searchTo) < 0 ||
        this.searchFrom === "" ||
        this.searchTo === "") &&
      !this.internalIsUsableRange
    ) {
      this.showInputValidationError = true;
    } else {
      this.showInputValidationError = false;
      let min;
      let max;
      if (this.internalIsUsableRange) {
        min = "0";
        max = userPoints.toString();
      } else {
        min = this.searchFrom;
        max = this.searchTo;
      }

      this.$emit("update:search-from", this.searchFrom);
      this.$emit("update:search-to", this.searchTo);
      LocalStorage.setItem("poductListMinPoint", min);
      LocalStorage.setItem("poductListMaxPoint", max);
      this.$emit("min-max-form", { min, max });
    }
  }

  getUserPoints() {
    const userPoints = LocalStorage.getItem("userPointsDetails")
      ? JSON.parse(LocalStorage.getItem("userPointsDetails") as string)
      : [];

    return userPoints.total_points
      ? userPoints.total_points - userPoints.thanks_exclusive_points
      : 0;
  }

  clickOnSubcategory(id: number) {
    this.$emit("update:showMainCategoryActive", false);
    this.$emit("click-subcategory-upper", id);
  }

  async created() {
    if (LocalStorage.getItem("usableRange") === "1") {
      this.internalIsUsableRange = true;
      this.disableMinMaxInputs = true;
    } else {
      this.disableMinMaxInputs = false;
      this.internalIsUsableRange = false;
    }
  }

  mounted() {
    const formElement = this.$refs.form as HTMLFormElement;
  }
}
</script>

<style lang="scss" scoped>
.active_category {
  color: red;
}
.p-the-b01-search-panel {
  background-color: $common-white;
  padding: rem(32) rem(24) rem(40);

  @include large-sp {
    display: none;
  }

  &__categories {
    margin: rem(16) 0 rem(24) rem(16);
    padding-bottom: rem(24);
    border-bottom: px(1) solid #b6b6b6;

    &__item {
      font-weight: bold;
      padding: rem(4) 0;
    }
  }

  &__form {
    &__selects {
      display: flex;
      align-items: center;
      padding: 0 rem(12);
    }

    &__select {
      flex: 1;
    }

    &__range {
      margin: 0 rem(4);
      font-weight: bold;
    }

    &__checkbox {
      margin: rem(12) rem(12) 0;

      &__label {
        font-size: rem(14);
        font-weight: bold;
      }
    }

    &__submit {
      margin-top: rem(32);
    }
  }
}
</style>
